<template>
    <div v-if="channelData" class="h-100">
        <b-container class="p-0" fluid>
            <b-row align-h="center">
                <b-col cols="12" md="10">
                    <b-link :to="{ name: 'settings-channels' }">
                        <font-awesome-icon icon="arrow-left" />
                        {{ $t("settings.channels.platform.back") }}
                    </b-link>
                    <h1 class="mt-1">{{ channelData.name }}</h1>
                    <hr />

                    <div v-if="isLoading" class="d-flex justify-content-center mb-3">
                        <b-spinner />
                    </div>

                    <validation-observer v-if="!isLoading" ref="validationRules" name="channel" tag="form">
                        <b-form class="mt-1">
                            <general :channel-data="channelData" />

                            <settings :channel-data="channelData" />

                            <automatic-replies :channel-data="channelData" />

                            <business-hours :channel-data="channelData" />
                            <b-button
                                :disabled="isSyncing"
                                class="mr-2 text-nowrap"
                                variant="primary"
                                @click="syncTemplates()"
                                v-if="channelData.provider === 'bird'"
                            >
                                <font-awesome-icon class="mr-50" icon="cloud-arrow-down" />
                                <span>{{
                                    isSyncing
                                        ? $t("settings.system.channels.whatsapp-bird-sync-templates.syncing")
                                        : $t("settings.system.channels.whatsapp-bird-sync-templates.sync")
                                }}</span>
                            </b-button>
                            <b-row class="my-2">
                                <b-col offset-md="3">
                                    <b-button
                                        :block="$store.getters['app/currentBreakPoint'] === 'xs'"
                                        :disabled="isSaving"
                                        class="d-flex justify-content-center mb-1"
                                        variant="primary"
                                        @click="submit()"
                                    >
                                        <b-spinner v-if="isSaving" class="mr-50" small></b-spinner>
                                        {{ $t("Save") }}
                                    </b-button>
                                </b-col>
                            </b-row>
                        </b-form>
                    </validation-observer>
                </b-col>
            </b-row>
        </b-container>
    </div>
</template>

<script>
import { BContainer, BCol, BRow, BAlert, BButton, BForm, BLink, BSpinner } from "bootstrap-vue";
import { ref } from "@vue/composition-api";
import router from "@/router";
import { ValidationObserver } from "vee-validate";
import axios from "@/libs/axios";

import General from "./components/general";
import Settings from "./components/settings";
import BusinessHours from "./components/businessHours";
import AutomaticReplies from "./components/automaticReplies";

import i18n from "@/libs/i18n";
import { useToast } from "vue-toastification/composition";
import ToastificationContent from "@/layouts/components/toastification/toastificationContent.vue";
import { useCustomersStore } from "@/store/useCustomersStore";

export default {
    components: {
        BContainer,
        BButton,
        BCol,
        BRow,
        BForm,
        BAlert,
        BLink,
        BSpinner,
        General,
        Settings,
        BusinessHours,
        AutomaticReplies,
        ValidationObserver,
    },
    setup() {
        const toast = useToast();
        const settingsToDelete = [];
        const channelData = ref();
        const isLoading = ref(false);
        const isSaving = ref(false);
        const { syncWhatsappTemplates } = useCustomersStore();
        const isSyncing = ref(false);

        const syncTemplates = () => {
            isSyncing.value = true;
            syncWhatsappTemplates()
                .then(() => {
                    toast({
                        component: ToastificationContent,
                        props: {
                            title: i18n.t("settings.system.card-title"),
                            text: i18n.t("settings.system.saving.success"),
                            icon: "check",
                            variant: "success",
                        },
                    });
                })
                .catch(() => {
                    toast({
                        component: ToastificationContent,
                        props: {
                            title: i18n.t("settings.system.card-title"),
                            text: i18n.t("settings.system.saving.error"),
                            icon: "cross",
                            variant: "danger",
                        },
                    });
                });

            setTimeout(() => {
                isSyncing.value = false;
            }, 1200000);
        };

        axios
            .get(`/channels/${router.currentRoute.params.id}`, {
                params: {
                    fields: "id,name,provider,platform,identity,auth,active",
                },
            })
            .then((response) => (channelData.value = response.data))
            .catch(() => toast.error(i18n.t("toast.read.error")))
            .finally(() => (isLoading.value = false));

        return {
            channelData,
            isLoading,
            isSaving,
            isSyncing,
            settingsToDelete,
            toast,
            syncTemplates,
        };
    },
    methods: {
        submit() {
            this.isSaving = true;

            return new Promise((resolve, reject) => {
                return this.$refs.validationRules.validate().then((success) => {
                    if (success) {
                        // Filter out settings with no value and emailOmitSaysimpleSignature
                        this.channelData.settings = this.channelData.settings.filter(
                            (setting) => setting.value && setting.name !== "emailOmitSaysimpleSignature"
                        );

                        const emailSignatureSetting = this.channelData.settings.find(
                            (setting) => setting.name === "email_signature"
                        );

                        // do it because we got the setting value "0" in that shape and we want booleans for api
                        this.channelData.settings = this.channelData.settings.reduce((acc, curr) => {
                            if (curr.name === "unassignmentAfterArchiving") {
                                curr.value = Boolean(Number(curr.value));
                            }
                            acc.push(curr);
                            return acc;
                        }, []);

                        if (
                            // Total signature can't be larger than 350kb, this number is relative to an asset of 250kb
                            emailSignatureSetting?.value &&
                            emailSignatureSetting.value.length > 350000
                        ) {
                            this.toast.error(
                                i18n.t("settings.channels.email.signature-exceeded-maximum-allowed-size", {
                                    entity: this.channelData.name,
                                })
                            );
                            this.isSaving = false;
                            return;
                        }

                        return axios
                            .put(`channels/${this.channelData.id}`, this.channelData)
                            .then(() => {
                                this.$router.push({ name: "settings-channels" }, () => {
                                    this.toast.success(
                                        i18n.t("toast.update.success", {
                                            entity: this.channelData.name,
                                        })
                                    );
                                });
                            })
                            .catch(() => {
                                this.toast.error(
                                    i18n.t("toast.update.error", {
                                        entity: this.channelData.name,
                                    })
                                );
                            });
                    } else {
                        reject();
                    }
                    reject();
                });
            })
                .catch(() => this.toast.error(i18n.t("toast.update.error", { entity: this.channelData.name })))
                .finally(() => (this.isSaving = false));
        },
    },
};
</script>

<style lang="scss">
@import "~@core/scss/vue/libs/vue-select.scss";
</style>
